import ExcelJS from 'exceljs';

export interface BayMappingRow {
  /** The raw fixture id from the floorplan (e.g. `ROW1.TABLE1`). */
  fixtureId: string;
  /** Display name of the zone the fixture belongs to (e.g. Drop Zone for GFC,
   *  Subcategory for WFM/Fresh). */
  zoneName: string;
  /** Pick path id / sequence order for the fixture. Required: the export
   *  intentionally omits fixtures that haven't been sequenced, so every row
   *  reaching the workbook has a value. */
  pickPathId: number;
}

const sanitizeForFileName = (s: string): string => s.replace(/[/\\:*?"<>|\s]/g, '_');

/**
 * Builds the .xlsx filename. Mirrors the SVG export naming so users can drop
 * them next to each other on disk.
 */
export function buildBayMappingFileName(storeId: string, date: string, department?: string): string {
  const deptSuffix = department ? `-${sanitizeForFileName(department)}` : '';
  return `Bay Mapping ${sanitizeForFileName(storeId)} ${sanitizeForFileName(date)}${deptSuffix}.xlsx`;
}

/**
 * Builds an Excel workbook with one row per bay, mapping it to its zone.
 * `sequenceLabel` is the program's display noun for the sequence column
 * (e.g. 'Pick Path' for GFC, 'Product Flow' for WFM/Fresh).
 */
export async function buildBayMappingWorkbook(
  rows: BayMappingRow[],
  zoneLabel: string,
  sequenceLabel: string,
): Promise<ExcelJS.Workbook> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'GS Category Management';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Bay Mapping');

  const header = sheet.addRow(['Fixture ID', zoneLabel, `${sequenceLabel} ID`]);
  header.font = { bold: true };

  // Sort by fixture id with a numeric-aware comparator so `ROW2.TABLE10` comes
  // after `ROW2.TABLE2`, not before.
  const sorted = [...rows].sort((a, b) =>
    a.fixtureId.localeCompare(b.fixtureId, undefined, {
      numeric: true,
      sensitivity: 'base',
    }),
  );
  for (const row of sorted) {
    sheet.addRow([row.fixtureId, row.zoneName, row.pickPathId]);
  }

  // Auto-fit column widths to the longest cell content for readability.
  sheet.columns.forEach((col) => {
    let max = 10;
    col.eachCell?.({ includeEmpty: false }, (cell) => {
      const len = String(cell.value ?? '').length;
      if (len > max) max = len;
    });
    // eslint-disable-next-line no-param-reassign
    col.width = max + 2;
  });

  return workbook;
}

/**
 * Triggers a browser download of the bay-to-zone Excel file. `zoneLabel` is
 * the program's display label for the zone column, `sequenceLabel` for the
 * sequence column (see `buildBayMappingWorkbook`).
 */
export async function downloadBayMappingExcel(
  rows: BayMappingRow[],
  fileName: string,
  zoneLabel: string,
  sequenceLabel: string,
): Promise<void> {
  const workbook = await buildBayMappingWorkbook(rows, zoneLabel, sequenceLabel);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
